import { supabase } from "@/integrations/supabase/client";

export type TransparencyConfig = {
  card_opacity: number;
  card_featured_opacity: number;
  card_blur: number;
  card_featured_blur: number;
  nav_opacity: number;
  footer_opacity: number;
  menu_opacity: number;
  menu_blur: number;
  menu_item_hover_opacity: number;
  menu_item_hover_blur: number;
};

export const DEFAULT_TRANSPARENCY: TransparencyConfig = {
  card_opacity: 45,
  card_featured_opacity: 65,
  card_blur: 20,
  card_featured_blur: 28,
  nav_opacity: 55,
  footer_opacity: 55,
  menu_opacity: 60,
  menu_blur: 20,
  menu_item_hover_opacity: 100,
  menu_item_hover_blur: 0,
};

export const fetchTransparencyConfig = async (): Promise<TransparencyConfig> => {
  const { data, error } = await supabase
    .from("marketing_transparency_settings")
    .select("*")
    .eq("id", true)
    .maybeSingle();
  if (error || !data) return DEFAULT_TRANSPARENCY;
  return {
    card_opacity: data.card_opacity,
    card_featured_opacity: data.card_featured_opacity,
    card_blur: data.card_blur,
    card_featured_blur: data.card_featured_blur,
    nav_opacity: data.nav_opacity,
    footer_opacity: data.footer_opacity,
    menu_opacity: (data as { menu_opacity?: number }).menu_opacity ?? DEFAULT_TRANSPARENCY.menu_opacity,
    menu_blur: (data as { menu_blur?: number }).menu_blur ?? DEFAULT_TRANSPARENCY.menu_blur,
    menu_item_hover_opacity: (data as { menu_item_hover_opacity?: number }).menu_item_hover_opacity ?? DEFAULT_TRANSPARENCY.menu_item_hover_opacity,
    menu_item_hover_blur: (data as { menu_item_hover_blur?: number }).menu_item_hover_blur ?? DEFAULT_TRANSPARENCY.menu_item_hover_blur,
  };
};

export const saveTransparencyConfig = async (cfg: TransparencyConfig): Promise<void> => {
  const { error } = await supabase
    .from("marketing_transparency_settings")
    .update({ ...cfg, updated_at: new Date().toISOString() })
    .eq("id", true);
  if (error) throw error;
};

/** Apply transparency values as CSS custom properties on <html>. */
export const applyTransparencyToDocument = (cfg: TransparencyConfig) => {
  const root = document.documentElement;
  root.style.setProperty("--mkt-card-opacity", String(cfg.card_opacity / 100));
  root.style.setProperty("--mkt-card-featured-opacity", String(cfg.card_featured_opacity / 100));
  root.style.setProperty("--mkt-card-blur", `${cfg.card_blur}px`);
  root.style.setProperty("--mkt-card-featured-blur", `${cfg.card_featured_blur}px`);
  root.style.setProperty("--mkt-nav-opacity", String(cfg.nav_opacity / 100));
  root.style.setProperty("--mkt-footer-opacity", String(cfg.footer_opacity / 100));
  root.style.setProperty("--mkt-menu-opacity", String(cfg.menu_opacity / 100));
  root.style.setProperty("--mkt-menu-blur", `${cfg.menu_blur}px`);
  root.style.setProperty("--mkt-menu-item-hover-opacity", String(cfg.menu_item_hover_opacity / 100));
  root.style.setProperty("--mkt-menu-item-hover-blur", `${cfg.menu_item_hover_blur}px`);
};

const PREVIEW_KEY = "mkt-transparency-preview";
export const getPreviewTransparency = (): TransparencyConfig | null => {
  try {
    const raw = localStorage.getItem(PREVIEW_KEY);
    return raw ? (JSON.parse(raw) as TransparencyConfig) : null;
  } catch {
    return null;
  }
};
export const setPreviewTransparency = (cfg: TransparencyConfig | null) => {
  if (cfg) localStorage.setItem(PREVIEW_KEY, JSON.stringify(cfg));
  else localStorage.removeItem(PREVIEW_KEY);
  window.dispatchEvent(new Event("mkt-transparency-preview-change"));
};
